import { WritableAtom, atom } from 'jotai';

/*
    This atom is used to coordinate the update of multiple atoms.
    It takes in an initial value and an array of dependencies.
    When the parent atom is updated, all the dependencies are updated with the new value.
    The dependencies are updated with the new value and the options passed to the parent atom.
    The options are passed to the dependencies in the same order as they are passed to the parent atom.

    Example:
    const deliveryMethodAtom = coordinatedAtom(
        DeliveryMethod.InstantFeedback,
        [oneAttemptAtom, requireNamesAtom, randomAnswersAtom, randomQuestionsAtom, showFeedbackAtom, showResultsAtom]
    );

    set(deliveryMethodAtom, DeliveryMethod.SpaceRace, true);
*/
export function coordinatedAtom<
  ParentAtomType,
  DependantAtomType,
  V extends [ParentAtomType, ...unknown[]]
>(
  initialValue: ParentAtomType,
  dependencies?: Array<WritableAtom<DependantAtomType, V, void>>
) {
  const internalAtom = atom(initialValue);
  return atom(
    (get) => get(internalAtom),
    (get, set, ...options: V) => {
      // Update base delivery method
      const [newValue] = options;
      set(internalAtom, newValue);

      // Auto-update all setting atoms to their new derived values
      (dependencies ?? []).forEach((dependency) => {
        dependency.write(get, set, ...options);
      });
    }
  );
}
